import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Camera, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useT } from "../i18n/LanguageProvider";

const PHOTOS = [
  {
    src: "https://customer-assets.emergentagent.com/job_future-tech-site-6/artifacts/e0sufj55_WhatsApp%20Image%202026-04-02%20at%204.55.09%20PM.jpeg",
    caption: "AVIRBHAV · Inauguration",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_future-tech-site-6/artifacts/6iljq1yx_WhatsApp%20Image%202026-04-02%20at%202.30.39%20PM.jpeg",
    caption: "Avirbhav · Football Champions",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_future-tech-site-6/artifacts/azcjuwbh_WhatsApp%20Image%202026-04-02%20at%202.30.39%20PM%20%282%29.jpeg",
    caption: "Kalayatra 2.0 · Arts Fest",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_future-tech-site-6/artifacts/rwsejyca_WhatsApp%20Image%202026-04-02%20at%202.30.39%20PM%20%283%29.jpeg",
    caption: "Live · Campus Concert",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_future-tech-site-6/artifacts/2sbgonf2_WhatsApp%20Image%202026-03-31%20at%205.59.58%20PM.jpeg",
    caption: "Annual Day · Lamp Lighting",
  },
];

const Card = ({ p, i, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(i)}
    className="relative mx-2 sm:mx-3 w-[200px] sm:w-[240px] md:w-[280px] h-[140px] sm:h-[170px] md:h-[190px] flex-shrink-0 rounded-lg overflow-hidden border border-[#00E5FF]/20 group cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-[#39FF14]"
    data-testid={`gallery-photo-${i}`}
    aria-label={`Open ${p.caption}`}
  >
    <img
      src={p.src}
      alt={p.caption}
      loading="lazy"
      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#04060a] via-[#04060a]/30 to-transparent" />
    <div className="absolute bottom-2 left-3 right-3 text-left">
      <div className="font-mono text-[9px] tracking-[0.2em] text-[#39FF14] uppercase">
        FRAME · {String(i + 1).padStart(2, "0")}
      </div>
      <div className="font-heading text-[11px] sm:text-xs text-white leading-tight mt-0.5">
        {p.caption}
      </div>
    </div>
    <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[#00E5FF] shadow-[0_0_8px_#00E5FF] pulse-dot" />
  </button>
);

const Lightbox = ({ open, index, onClose, onPrev, onNext }) => {
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handler);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handler);
    };
  }, [open, onClose, onPrev, onNext]);

  if (index === null) return null;
  const photo = PHOTOS[index];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8 bg-[#04060a]/95 backdrop-blur-md"
          onClick={onClose}
          data-testid="lightbox-overlay"
        >
          {/* grid bg */}
          <div className="absolute inset-0 bg-grid mask-radial opacity-20 pointer-events-none" />

          {/* Close */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full bg-[#04060a]/80 border border-[#00E5FF]/40 flex items-center justify-center text-white hover:bg-[#39FF14] hover:text-[#04060a] hover:border-[#39FF14] transition"
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Prev */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#04060a]/80 border border-[#00E5FF]/40 flex items-center justify-center text-white hover:bg-[#00E5FF] hover:text-[#04060a] hover:border-[#00E5FF] transition"
            aria-label="Previous"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          {/* Next */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#04060a]/80 border border-[#00E5FF]/40 flex items-center justify-center text-white hover:bg-[#00E5FF] hover:text-[#04060a] hover:border-[#00E5FF] transition"
            aria-label="Next"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Image */}
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="relative max-w-[92vw] max-h-[85vh] w-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute -inset-1 bg-gradient-to-br from-[#00E5FF]/50 to-[#39FF14]/40 blur-xl rounded-2xl" />
            <div className="relative rounded-xl overflow-hidden border border-[#00E5FF]/40 bg-[#07090f]">
              <img
                src={photo.src}
                alt={photo.caption}
                className="block max-w-[92vw] max-h-[78vh] w-auto h-auto object-contain"
                data-testid="lightbox-image"
              />
              <div className="absolute bottom-0 inset-x-0 p-4 md:p-5 bg-gradient-to-t from-[#04060a] via-[#04060a]/80 to-transparent flex items-end justify-between gap-4">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.3em] text-[#39FF14] uppercase mb-1">
                    FRAME · {String(index + 1).padStart(2, "0")} /{" "}
                    {String(PHOTOS.length).padStart(2, "0")}
                  </div>
                  <div className="font-heading text-base md:text-xl text-white">
                    {photo.caption}
                  </div>
                </div>
                <div className="hidden sm:block font-mono text-[10px] text-white/45 tracking-[0.25em] uppercase">
                  ESC · CLOSE / ← → NAV
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Gallery = () => {
  const { t } = useT();
  const [openIdx, setOpenIdx] = useState(null);

  const open = useCallback((i) => setOpenIdx(i), []);
  const close = useCallback(() => setOpenIdx(null), []);
  const prev = useCallback(
    () => setOpenIdx((i) => (i === null ? null : (i - 1 + PHOTOS.length) % PHOTOS.length)),
    []
  );
  const next = useCallback(
    () => setOpenIdx((i) => (i === null ? null : (i + 1) % PHOTOS.length)),
    []
  );

  return (
    <section
      id="gallery"
      className="relative py-16 md:py-20 border-t border-white/5 overflow-hidden"
      data-testid="gallery-section"
    >
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 mb-8 md:mb-10">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className="flex items-end justify-between gap-4 flex-wrap"
        >
          <div>
            <div className="kicker mb-3">{t("gallery.kicker")}</div>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-bold text-white tracking-tight leading-[1.05]">
              {t("gallery.headA")}{" "}
              <span className="text-[#00E5FF] text-glow-cyan">
                {t("gallery.headB")}
              </span>
              .
            </h2>
            <p className="text-xs md:text-sm text-[#94a3b8] mt-3 font-mono">
              {t("gallery.hint")}
            </p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 border border-[#39FF14]/30 bg-[#39FF14]/5 rounded-full">
            <Camera className="w-3.5 h-3.5 text-[#39FF14]" />
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#39FF14]">
              {PHOTOS.length} {t("gallery.snapshots")}
            </span>
          </div>
        </motion.div>
      </div>

      <div className="marquee-fade">
        <Marquee gradient={false} speed={28} pauseOnHover>
          {PHOTOS.map((p, i) => (
            <Card key={`a-${i}`} p={p} i={i} onClick={open} />
          ))}
        </Marquee>
      </div>

      <div className="marquee-fade mt-3 md:mt-4">
        <Marquee gradient={false} speed={22} direction="right" pauseOnHover>
          {PHOTOS.slice().reverse().map((p, i) => {
            const realIdx = PHOTOS.length - 1 - i;
            return <Card key={`b-${i}`} p={p} i={realIdx} onClick={open} />;
          })}
        </Marquee>
      </div>

      <Lightbox
        open={openIdx !== null}
        index={openIdx}
        onClose={close}
        onPrev={prev}
        onNext={next}
      />
    </section>
  );
};

export default Gallery;
